export interface FailedInstance {
  solId: string
  customerId: string
}

/**
 * Parses the { failedInstances: [{ solId, customerId }] } metadata payload an Error Log entry
 * may carry (currently: the feature toggle refresh cron), so its message can link each instance
 * id straight to that instance's product page instead of showing a bare id.
 *
 * Returns an empty array for null/undefined metadata, metadata that isn't valid JSON, or JSON
 * that doesn't have this shape — every case falls back to rendering the message as plain text.
 */
export function parseFailedInstances(metadataRaw: string | null | undefined): FailedInstance[] {
  if (!metadataRaw) return []
  try {
    const parsed = JSON.parse(metadataRaw)
    if (!Array.isArray(parsed?.failedInstances)) return []
    return parsed.failedInstances.filter(
      (i: unknown): i is FailedInstance =>
        typeof i === 'object' && i !== null
        && typeof (i as FailedInstance).solId === 'string'
        && typeof (i as FailedInstance).customerId === 'string'
    )
  } catch {
    return []
  }
}

/** One chunk of a split message: either plain text, or a solId that should become a link. */
export type MessagePart =
  | { type: 'text'; text: string }
  | { type: 'instance'; solId: string; customerId: string }

/**
 * Splits `message` on every solId named in `failedInstances`, so only those substrings become
 * links — the surrounding sentence stays plain text. An instance whose solId never appears in
 * the message (message and metadata drifted, or a malformed entry) is simply never matched, so
 * it just doesn't produce a link rather than breaking the split.
 */
export function splitMessageOnFailedInstances(message: string, failedInstances: FailedInstance[]): MessagePart[] {
  if (failedInstances.length === 0) return [{type: 'text', text: message}]

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  // Regex alternation matches the first alternative that fits, not the longest one — so if one
  // solId is a string-prefix of another (e.g. "sol-1" of "sol-10"), sorting shortest-first would
  // match "sol-1" inside "sol-10" and leave a stray "0" as plain text. Longest-first guarantees
  // the more specific id is always tried first.
  const sortedInstances = [...failedInstances].sort((a, b) => b.solId.length - a.solId.length)
  const pattern = new RegExp(`(${sortedInstances.map(i => escapeRegExp(i.solId)).join('|')})`, 'g')

  return message.split(pattern).map((part): MessagePart => {
    const instance = failedInstances.find(f => f.solId === part)
    return instance
      ? {type: 'instance', solId: instance.solId, customerId: instance.customerId}
      : {type: 'text', text: part}
  })
}
